/** A circuit, drawn from what the community actually surveyed.
 *
 * The mocks draw every track as one stylised loop with a thick stroke, which
 * is a placeholder standing in for geometry that did not exist yet. The real
 * thing is two ordered border polylines — left and right, each split into
 * runs wherever the survey has a gap — plus the start/finish line averaged
 * from recorded lap rollovers. So the road is not a stroke here; it is the
 * space between two lines, and a partially surveyed circuit draws as exactly
 * the fragments that were driven. That is the honest picture and it is also
 * the useful one: a gap in the outline is the thing the Tracks page is
 * reporting.
 *
 * Coordinates are world metres. `x` goes straight across, `z` is negated
 * because SVG counts y downwards and the outline should read the same way up
 * as the map in the game. The viewBox is fitted to the geometry with a margin
 * rather than fixed, so a 20 km Nordschleife and a 2 km Tsukuba both fill
 * their card; strokes are `non-scaling-stroke`, so a line stays the same
 * weight on screen however far the fit had to zoom.
 *
 * A circuit with no compiled geometry — surveyed but never published, or
 * merely known to exist — falls back to the design's stylised loop, so the
 * card keeps its shape while the map is still being filled in.
 *
 * The fitting maths is exported and pure. It has no React in it and can be
 * tested by feeding it numbers.
 */

import type { CSSProperties } from 'react';
import type { Polyline, Segment, TrackGeometry, TrackState } from '@/lib/types';

// ── the maths ─────────────────────────────────────────────────────────────

/** A point in the drawing's own space: world x, and world z negated. */
export type Point = [number, number];

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** World `[x, z, y]` to drawing `[x, -z]`. Elevation is not drawn — the
 *  third component is null on every metre surveyed before the logger started
 *  recording it, and nothing here needs it. */
export function toPoint(vertex: ReadonlyArray<number | null>): Point {
  return [vertex[0] ?? 0, -(vertex[1] ?? 0)];
}

export function runToPoints(run: Polyline): Point[] {
  return run.map(toPoint);
}

export function boundsOfPoints(points: readonly Point[]): Box | null {
  let box: Box | null = null;
  for (const point of points) {
    const [x, y] = point;
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (!box) {
      box = { minX: x, minY: y, maxX: x, maxY: y };
      continue;
    }
    if (x < box.minX) box.minX = x;
    if (y < box.minY) box.minY = y;
    if (x > box.maxX) box.maxX = x;
    if (y > box.maxY) box.maxY = y;
  }
  return box;
}

export function unionBox(a: Box | null, b: Box | null): Box | null {
  if (!a) return b;
  if (!b) return a;
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** Whether there is anything to draw. An empty `borders` is what a circuit
 *  looks like before its first bundle is published. */
export function hasGeometry(geometry: TrackGeometry | null | undefined): geometry is TrackGeometry {
  if (!geometry?.borders) return false;
  const left = geometry.borders.L ?? [];
  const right = geometry.borders.R ?? [];
  return [...left, ...right].some((run) => run.length >= 2);
}

export function geometryBounds(geometry: TrackGeometry): Box | null {
  let box: Box | null = null;
  for (const run of [...(geometry.borders.L ?? []), ...(geometry.borders.R ?? [])]) {
    box = unionBox(box, boundsOfPoints(runToPoints(run)));
  }
  if (geometry.finish) {
    const [x1, z1, x2, z2] = geometry.finish;
    box = unionBox(
      box,
      boundsOfPoints([
        [x1, -z1],
        [x2, -z2],
      ]),
    );
  }
  return box;
}

/** Fit a box into a viewBox with a margin, as a fraction of the longer side.
 *  A degenerate box — one point, or a straight line — is widened so the
 *  result is never zero-sized and the browser never divides by it. */
export function fitViewBox(box: Box, padFraction = 0.06): ViewBox {
  const rawWidth = box.maxX - box.minX;
  const rawHeight = box.maxY - box.minY;
  const longest = Math.max(rawWidth, rawHeight, 1);
  const pad = longest * padFraction;

  const width = Math.max(rawWidth, longest * 0.02) + pad * 2;
  const height = Math.max(rawHeight, longest * 0.02) + pad * 2;

  return {
    x: box.minX - (width - rawWidth) / 2,
    y: box.minY - (height - rawHeight) / 2,
    width,
    height,
  };
}

export function viewBoxString(view: ViewBox): string {
  const round = (value: number) => Number(value.toFixed(2));
  return `${round(view.x)} ${round(view.y)} ${round(view.width)} ${round(view.height)}`;
}

/** `M x y L x y …`. An open run — the borders are not closed paths unless the
 *  survey happened to close them, and pretending otherwise would draw a line
 *  across a gap the coverage figure says is there. */
export function pathFromPoints(points: readonly Point[]): string {
  if (points.length < 2) return '';
  return points
    .map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
}

export interface OutlinePaths {
  left: string[];
  right: string[];
  /** The start/finish line in drawing space, or null. */
  finish: [Point, Point] | null;
}

export function pathsFromGeometry(geometry: TrackGeometry): OutlinePaths {
  const draw = (runs: Polyline[] | undefined): string[] =>
    (runs ?? []).map((run) => pathFromPoints(runToPoints(run))).filter(Boolean);

  const finishOf = (segment: Segment | null): [Point, Point] | null => {
    if (!segment) return null;
    const [x1, z1, x2, z2] = segment;
    if (![x1, z1, x2, z2].every((value) => Number.isFinite(value))) return null;
    return [
      [x1, -z1],
      [x2, -z2],
    ];
  };

  return {
    left: draw(geometry.borders.L),
    right: draw(geometry.borders.R),
    finish: finishOf(geometry.finish),
  };
}

/** The longest surveyed run, which is what the travelling dash is drawn
 *  along: the dash is decoration, and it should decorate the part of the
 *  circuit that is actually known. */
export function longestRun(geometry: TrackGeometry): Point[] | null {
  let best: Point[] | null = null;
  for (const run of [...(geometry.borders.L ?? []), ...(geometry.borders.R ?? [])]) {
    if (!best || run.length > best.length) best = runToPoints(run);
  }
  return best && best.length >= 2 ? best : null;
}

/** The design's stylised loop, for a circuit with nothing compiled yet. */
export const PLACEHOLDER_VIEWBOX = '-60 -60 520 380';
export const PLACEHOLDER_PATH =
  'M 60 200 L 300 200 C 340 200 360 180 350 150 L 330 110 C 320 80 340 60 310 50 ' +
  'L 220 40 C 190 38 180 60 200 80 L 230 105 C 245 120 230 140 205 135 L 140 120 ' +
  'C 110 115 90 95 70 100 C 40 106 30 140 45 165 C 52 180 45 200 60 200 Z';

// ── the component ─────────────────────────────────────────────────────────

const STATE_STROKE: Record<TrackState, string> = {
  published: 'var(--color-neutral-400)',
  pending: 'var(--color-neutral-600)',
  held: 'var(--color-neutral-600)',
  surveying: 'var(--color-neutral-800)',
};

export interface TrackOutlineProps {
  /** The compiled document. Absent or empty draws the placeholder. */
  geometry?: TrackGeometry | null;
  /** Picks the stroke from the circuit's state, as the Tracks cards do. */
  state?: TrackState;
  /** Overrides the state colour — a reference line, a selected session. */
  stroke?: string;
  /** Screen pixels, whatever the fit: the strokes do not scale. */
  strokeWidth?: number;
  /** Margin round the geometry, as a fraction of its longer side. */
  padFraction?: number;
  /** The accent dash that walks the outline, six seconds to the lap. */
  animated?: boolean;
  /** Draws the start/finish line across the road. */
  showFinish?: boolean;
  /** Labels that line `S/F`. */
  finishLabel?: boolean;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  /** Gives the drawing an accessible name. Without one it is decoration, and
   *  the card's own title already says which circuit this is. */
  title?: string;
}

export function TrackOutline({
  geometry,
  state = 'published',
  stroke,
  strokeWidth = 1.6,
  padFraction = 0.06,
  animated = false,
  showFinish = true,
  finishLabel = false,
  height = 96,
  className,
  style,
  title,
}: TrackOutlineProps) {
  const colour = stroke ?? STATE_STROKE[state];
  const shared = {
    className,
    role: title ? ('img' as const) : ('presentation' as const),
    'aria-label': title,
    'aria-hidden': title ? undefined : true,
    style: { width: '100%', height, display: 'block', overflow: 'visible' as const, ...style },
  };

  if (!hasGeometry(geometry)) {
    return (
      <svg viewBox={PLACEHOLDER_VIEWBOX} {...shared}>
        <path
          d={PLACEHOLDER_PATH}
          fill="none"
          stroke={colour}
          strokeWidth={strokeWidth * 8}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity={0.55}
        />
        {animated ? (
          <path
            d={PLACEHOLDER_PATH}
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="1.5"
            strokeDasharray="6 394"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            style={{
              animation: 'dash 6s linear infinite',
              filter: 'drop-shadow(0 0 6px var(--color-accent))',
            }}
          />
        ) : null}
      </svg>
    );
  }

  const box = geometryBounds(geometry);
  if (!box) return <svg viewBox={PLACEHOLDER_VIEWBOX} {...shared} />;

  const view = fitViewBox(box, padFraction);
  const paths = pathsFromGeometry(geometry);
  const dashRun = animated ? longestRun(geometry) : null;
  // Text inside a fitted viewBox has to be sized in that box's own units, or
  // a 20 km circuit would render the label at a hundredth of its height.
  const labelSize = view.width * 0.03;

  return (
    <svg viewBox={viewBoxString(view)} {...shared}>
      {[...paths.left, ...paths.right].map((d, index) => (
        <path
          key={index}
          d={d}
          fill="none"
          stroke={colour}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {showFinish && paths.finish ? (
        <line
          x1={paths.finish[0][0]}
          y1={paths.finish[0][1]}
          x2={paths.finish[1][0]}
          y2={paths.finish[1][1]}
          stroke="var(--color-neutral-300)"
          strokeWidth={strokeWidth * 1.4}
          strokeLinecap="butt"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}

      {finishLabel && paths.finish ? (
        <text
          x={(paths.finish[0][0] + paths.finish[1][0]) / 2}
          y={Math.max(paths.finish[0][1], paths.finish[1][1]) + labelSize * 1.6}
          fill="var(--color-neutral-600)"
          fontSize={labelSize}
          textAnchor="middle"
          fontFamily="var(--font-body)"
        >
          S/F
        </text>
      ) : null}

      {dashRun ? (
        <path
          d={pathFromPoints(dashRun)}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="1.5"
          strokeDasharray="6 394"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          style={{
            animation: 'dash 6s linear infinite',
            filter: 'drop-shadow(0 0 6px var(--color-accent))',
          }}
        />
      ) : null}
    </svg>
  );
}

export default TrackOutline;
